/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link, MapInfoWindow, MapMarker, MapComponent, MapMarkerCluster } from '@marriott/mi-ui-library';
import { GOOGLE_MAP_VAL_KEY } from '@marriott/shared/mi-helper-utils';
import clsx from 'clsx';

import { svgIcon, DEFAULT_ZOOM_LEVEL } from '../../../modules/constants/HotelDirectory.constants';
import {
  callFitBounds,
  customClusterCalculator,
  fitBounds,
  formatExitURL,
} from '../HotelDirectoryMap/HotelDirectoryMapHelper';
import { useEffect, useState } from 'react';
import {
  HotelDirectoryDataType,
  InfoWindowDataList,
  MapContainerProps,
} from '../HotelDirectoryMap/HotelDirectoryMap.types';

const encodedSvg = btoa(svgIcon);
const dataUrl = 'data:image/svg+xml;base64,' + encodedSvg;
const customClusterStyles = [
  {
    url: dataUrl,
    textColor: 'black',
    background: 'red', // Background color for the cluster marker
    width: 73,
    height: 73,
    textSize: 14,
    fontWeight: '500',
  },
];
const mapOptions = {
  fullscreenControl: true,
  mapTypeControl: true,
  zoomControl: true,
  gestureHandling: 'cooperative',
  mapId: 'ec5bb275520a7fc8',
  streetViewControl: true,
};

const GoogleMapClusters = ({
  center,
  markers,
  mapHeight,
  zoom,
  isMapLoaded,
  setIsMapLoaded,
  clustererRef,
  selectedRegion,
  isFiltersApplied,
  stateORCountryList,
  isDesktopView,
  openInfoWindow,
  infoWindowData,
  setOpenInfoWindow,
  setInfoWindowData,
}: MapContainerProps) => {
  const [mapRef, setMapRef] = useState<any>(null);
  const [markerRegionData, setMarkerRegionData] = useState<InfoWindowDataList>();
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);

  const onClusterMouseOver = (cluster: any) => {
    const clusterNew = { ...cluster };
    setOpenInfoWindow(true);
    const markerPosition = { lat: clusterNew?.getCenter()?.lat(), lng: clusterNew?.getCenter()?.lng() };

    setInfoWindowData({ ...markerPosition, subRegions: [clusterNew?.getMarkers()] });
  };

  useEffect(() => {
    //function to assign set boundaries
    if (isMapLoaded && mapRef && bounds) {
      fitBounds(mapRef, bounds);
      const boundValues = mapRef?.getBounds();
      for (const key in boundValues) {
        if (
          Object?.getOwnPropertyDescriptor(boundValues, 'lo') &&
          Object?.getOwnPropertyDescriptor(boundValues, 'hi')
        ) {
          const loValue = boundValues[key]?.lo;
          const hiValue = boundValues[key]?.hi;
          if (Number(loValue?.toFixed()) === Number(hiValue?.toFixed())) {
            mapRef?.setZoom(DEFAULT_ZOOM_LEVEL);
          }
        }
      }
    }
  }, [isMapLoaded, mapRef, bounds]);

  useEffect(() => {
    //function to set boundaries for maps based on the markers latitude and longitude
    callFitBounds(stateORCountryList, isMapLoaded, setBounds);
  }, [stateORCountryList, isMapLoaded, setBounds]);

  useEffect(() => {
    const tempMarkerRegionData: InfoWindowDataList = [];
    markers?.forEach((markerItem: HotelDirectoryDataType) => {
      infoWindowData?.subRegions?.length &&
        (infoWindowData?.subRegions[0] as unknown as InfoWindowDataList)?.forEach((item: any) => {
          if (markerItem.latitude === item?.position?.lat() && markerItem.longitude === item?.position?.lng()) {
            tempMarkerRegionData.push({
              regionName: markerItem?.label,
              count: markerItem?.count,
              regionCode: markerItem?.code,
            });
          }
        });
    });

    setMarkerRegionData(tempMarkerRegionData);
  }, [infoWindowData]);

  useEffect(() => {
    //using setTimeout to wait for the element to render on DOM before applying focus to it
    const timeoutHandler = setTimeout(() => {
      (document.getElementsByClassName('gm-style-iw-c')?.[0] as HTMLElement)?.focus(); // this focuses on the info window so that upon pressing tab first element to focus will be CROSS CTA
    }, 100);
    return () => {
      clearTimeout(timeoutHandler);
    };
  }, [markerRegionData]);

  return (
    <MapComponent
      googleMapsApiKey={GOOGLE_MAP_VAL_KEY}
      mapId="ec5bb275520a7fc8"
      mapHeight={mapHeight}
      mapContainerStyle={{ height: '100%', width: '100%' }}
      center={center}
      zoom={zoom}
      clickableIcons={false}
      onClick={() => {
        setOpenInfoWindow(false);
      }}
      onDrag={() => {
        setOpenInfoWindow(false);
      }}
      onZoomChanged={() => {
        setOpenInfoWindow(false);
      }}
      children={
        <MapMarkerCluster
          averageCenter={false}
          onMouseOver={onClusterMouseOver}
          calculator={customClusterCalculator}
          gridSize={80}
          options={{
            imagePath: '', // Empty string to disable default cluster icon
            styles: customClusterStyles, // Use the custom cluster styles
          }}
          onLoad={clusterer => {
            clustererRef.current = clusterer;
            if (!isDesktopView) {
              google.maps.event.addListener(clustererRef.current, 'clusterclick', (clusterer: any) => {
                onClusterMouseOver(clusterer);
              });
            }
          }}
          zoomOnClick={isDesktopView ? true : false}
        >
          {(clusterer: any) => (
            <>
              {markers?.map((marker: any) => (
                <div>
                  <MapMarker
                    icon={{ url: '', scaledSize: new google.maps.Size(40, 40) }}
                    label={{
                      text: marker.count?.toString(),
                      color: 'black',
                      fontSize: '14px',
                      className: 'oval',
                    }}
                    position={{ lat: marker.latitude, lng: marker.longitude }}
                    clusterer={clusterer}
                    onClick={() => {
                      const url = formatExitURL(marker.code, marker.label, selectedRegion.code, isFiltersApplied);
                      window.location.href = url;
                    }}
                  ></MapMarker>
                </div>
              ))}
              {openInfoWindow ? (
                <MapInfoWindow
                  options={{ ariaLabel: 'map-info-window' }}
                  position={{ lat: infoWindowData.lat, lng: infoWindowData.lng }}
                  onCloseClick={() => {
                    setOpenInfoWindow(false);
                  }}
                  children={
                    <span>
                      {markerRegionData?.map((item: any, index: number) => {
                        return (
                          <div
                            className={clsx(
                              `link-container ${index === markerRegionData.length - 1 ? 'pb-0 mb-0' : ''}`
                            )}
                          >
                            <Link
                              linkClassName={clsx('t-font-xs', 'link-custom')}
                              linkHref={formatExitURL(
                                item.regionCode,
                                item.regionName,
                                selectedRegion.code,
                                isFiltersApplied
                              )}
                              target="_self"
                              key={item?.regionName + ' (' + item?.count + ')'}
                            >
                              {item?.regionName + ' (' + item?.count + ')'}
                            </Link>
                          </div>
                        );
                      })}
                    </span>
                  }
                ></MapInfoWindow>
              ) : (
                <div></div>
              )}
            </>
          )}
        </MapMarkerCluster>
      }
      options={mapOptions}
      onLoad={map => {
        setMapRef(map);
        setIsMapLoaded(true);
      }}
    ></MapComponent>
  );
};

export default GoogleMapClusters;
